import { useMemoizedFn } from 'ahooks';
import { TableProps } from 'antd';
import { SortOrder, SorterResult } from 'antd/lib/table/interface';
import { routesMap } from 'app/Router';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { RevxTable } from 'components';
import { RevxButton } from 'components/RevxButton';
import RevxFlexLayout from 'components/RevxFlexLayout';
import { RevxGalleryImg } from 'components/RevxGalleryImg';
import { RevxInputNumber } from 'components/RevxInputNumber';
import { SectionHeading } from 'components/RevxTypography';
import { GallerySku } from 'domains/gallerySkuDomain';
import { hasGalleryDesignSize } from 'utils/galleryDesignSize';
import { hasGallerySkuFilter } from 'utils/gallerySkuFilter';
import {
  useCanvaDesignSize,
  useCanvaMatchView,
  useCanvaPublishDraft,
  useGallerySkuFilter,
} from 'hooks';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { ALBERTSONS_ORGS, JOB_MAX_QUANTITY } from 'utils/constants';
import './CompanyList.less';
import { CompanyModal } from './CompanyModal';
import { CompanyRequestProductCard } from './CompanyRequestProductCard';
import { SkuMatchChips } from './SkuMatchChips';

export const CompanyList = () => {
  const { selectedSkuType, skuTypes } = useAppSelector(
    (state) => state.companyGallery
  );
  const dispatch = useAppDispatch();
  const filter = useGallerySkuFilter();
  // Ranks the whole result by how well each SKU fits the parked design; empty on a normal visit.
  const { designSize, loading: designSizeLoading } = useCanvaDesignSize();
  // The match landing view: products that can hold the design, gathered from every category.
  const { isMatchView, isCanvaPublish } = useCanvaMatchView();
  const [loading, setLoading] = useState<boolean>(false);
  const [items, setItems] = useState<GallerySku[]>([]);
  const [open, setOpen] = useState(false);
  const [sku, setSku] = useState<GallerySku>();
  const [sortedInfo, setSortedInfo] = useState<SorterResult<any>>({});
  // Bumped per load, so the response for a category the user has left cannot land last and
  // replace the one they are looking at.
  const loadGenerationRef = useRef(0);
  // const dispatch = useAppDispatch();
  // const history = useHistory();
  // const { t } = useTranslation();

  const defaultSkuItem = useMemoizedFn(async () => {
    const generation = ++loadGenerationRef.current;
    const isCurrent = () => generation === loadGenerationRef.current;
    setLoading(true);
    try {
      // The preloaded skuTypes hold every company SKU, so they can only stand in
      // for the request while no filter narrows the gallery down -- and while nothing reorders
      // it either. They are loaded without a design size, so serving them on a Canva visit
      // would silently drop the size ranking the visit exists for.
      if (
        !selectedSkuType &&
        skuTypes.length > 0 &&
        !hasGallerySkuFilter(filter) &&
        !hasGalleryDesignSize(designSize)
      ) {
        setItems(skuTypes);
      } else {
        const { content } = await dispatch.gallery.getCompanySkusByLabel({
          // Never scoped to a company category on a Canva visit, in either state -- see
          // CompanyCard for why -1 empties the result for an org with no company labels.
          companyLabelId: isCanvaPublish
            ? undefined
            : selectedSkuType?.skuLabel!.id || -1,
          size: 10000,
          // A Canva visit compares against every active SKU in the org, so a tag or id filter
          // from the hand-off must not narrow the field -- in either state.
          filter: isCanvaPublish ? undefined : filter,
          designSize,
          matchedOnly: isMatchView,
        });
        if (!isCurrent()) {
          return;
        }
        setItems(content);
      }
    } catch {
    } finally {
      if (isCurrent()) {
        setLoading(false);
      }
    }
  });

  useEffect(() => {
    // Wait for the parked design's size before asking for the list: it decides the order of the
    // whole result, so loading first would render one order and then reshuffle it.
    if (designSizeLoading) {
      setLoading(true);
      return;
    }
    setLoading(true);
    defaultSkuItem();
  }, [
    defaultSkuItem,
    selectedSkuType,
    skuTypes,
    filter,
    designSize,
    designSizeLoading,
    isMatchView,
  ]);

  const handleChange: TableProps<any>['onChange'] = (
    pagination,
    filters,
    sorter
  ) => {
    setSortedInfo(sorter as SorterResult<any>);
  };

  const columns = [
    {
      width: 710,
      title: () => {
        return <div className="company-list-header_title">Item Name</div>;
      },
      dataIndex: 'skuLabel',
      key: 'skuLabel.name',
      sorter: (a: GallerySku, b: GallerySku) => a.name.localeCompare(b.name),
      sortOrder:
        sortedInfo.columnKey === 'skuLabel.name' ? sortedInfo.order : null,
      sortDirections: ['descend' as unknown as SortOrder],
      ellipsis: true,
      render: (text: string, record: GallerySku) => {
        return (
          <RevxFlexLayout
            onClick={() => {
              if (record.preConfigured) {
                // history.push(`/gallery/${record.skuLabel.id}`);
                setOpen(true);
                setSku(record);
              }
            }}
          >
            <div
              className="company-list_image"
              style={{ cursor: record.preConfigured ? 'pointer' : 'default' }}
            >
              {record.images[0] ? (
                <RevxGalleryImg src={record.images[0]} />
              ) : (
                <span className="img"></span>
              )}
            </div>
            <div className="company-list_name">
              <SectionHeading
                className="company-list_title"
                style={{ cursor: record.preConfigured ? 'pointer' : 'default' }}
                ellipsis={{
                  tooltip: record.name,
                }}
              >
                {record.name}
              </SectionHeading>
              <SkuMatchChips sku={record} />
            </div>
          </RevxFlexLayout>
        );
      },
    },
    {
      title: 'Item Type',
      dataIndex: 'skuLabel',
      key: 'skuLabel.type',
      sorter: (a: GallerySku, b: GallerySku) =>
        (a.typeName || '').localeCompare(b.typeName || ''),
      sortOrder:
        sortedInfo.columnKey === 'skuLabel.type' ? sortedInfo.order : null,
      // sortDirections: ['descend' as unknown as SortOrder],
      ellipsis: true,
      render: (text: string, record: GallerySku) => {
        return (
          <SectionHeading
            className="company-list_type"
            ellipsis={{
              tooltip: record.typeName,
            }}
          >
            {record.typeName}
          </SectionHeading>
        );
      },
    },
    {
      title: 'Quantity',
      dataIndex: 'skuLabel',
      key: 'skuLabel.skuCount',
      width: 300,
      render: (text: string, record: GallerySku) => GalleryListQuantity(record),
    },
  ];
  return (
    <>
      <div className="company-list">
        <RevxTable<GallerySku>
          onChange={handleChange}
          loading={loading}
          columns={columns}
          dataSource={items}
          pagination={false}
          size="small"
        />
      </div>
      <div className="company-list_request-product">
        <CompanyRequestProductCard />
      </div>
      <CompanyModal open={open} setOpen={setOpen} sku={sku} setSku={setSku} />
    </>
  );
};

export const GalleryListQuantity = (props: GallerySku) => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { t } = useTranslation();
  const [quantity, setQuantity] = useState<number>(props.defaultQty || 0);
  const [loading, setLoading] = useState<boolean>(false);
  const orgId = useAppSelector((state) => state.app.orgId);
  // Design parked by the Canva app: every pick here finishes that publish instead.
  const { isCanvaPublish, publishWithSku } = useCanvaPublishDraft();
  const setCurrentSkuType = async ({
    id,
    preConfigured,
    defaultQty,
  }: GallerySku) => {
    if (isCanvaPublish) {
      await publishWithSku(Number(id));
      return;
    }
    if (preConfigured) {
      return;
    }
    try {
      const configItem = await dispatch.jobConfig.startJobConfig({
        skuId: id,
        quantity: defaultQty,
        isExpert: false,
      });
      history.push(routesMap.jobConfig.path + '/' + configItem.id);
      dispatch.companyGallery.setSelectedSkuType(null);
    } catch (e) {
      throw new Error('error');
    }
  };
  return (
    <>
      {props.preConfigured && !isCanvaPublish ? (
        <RevxFlexLayout justify="space-between">
          <RevxInputNumber
            style={{ height: '28px', maxWidth: '100px' }}
            isInteger
            min={1}
            max={JOB_MAX_QUANTITY}
            value={quantity}
            onChange={(e) => setQuantity(e)}
          />
          <RevxButton
            size="middle"
            type="primary"
            brandGreen
            className="company-list_addToCart"
            loading={loading}
            onClick={async () => {
              try {
                setLoading(true);
                const cartLineItem = await dispatch.jobConfig.directlyAddToCart(
                  {
                    skuId: Number(props.id),
                    quantity: quantity,
                    isExpert: false,
                    directlyAddToCart: true,
                  }
                );
                // history.push(routesMap.shoppingCart.path);
                await dispatch.cart.getList();
                dispatch.cart.updateCartItem(cartLineItem);
              } catch {
              } finally {
                setLoading(false);
              }
            }}
          >
            {t('gallery.skuList.addToCart')}
          </RevxButton>
        </RevxFlexLayout>
      ) : (
        <RevxFlexLayout justify="space-between" align="center">
          <div
            style={{
              width: '60px',
              textAlign: 'center',
            }}
          >
            --
          </div>
          <RevxButton
            size="middle"
            type="primary"
            className="company-list_getStarted"
            onClick={async () => {
              try {
                return await setCurrentSkuType(props);
              } catch {
                throw new Error('error');
              }
            }}
          >
            {ALBERTSONS_ORGS.includes(orgId)
              ? t('gallery.skuList.customize')
              : t('gallery.skuList.choose-options')}
          </RevxButton>
        </RevxFlexLayout>
      )}
    </>
  );
};
